import { StarsButton, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import type { ChangeEvent } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { ModalContextValue } from '@swf/components/StarsModal/Context';
import { ModalContext } from '@swf/components/StarsModal/Context';
import Body from '@swf/components/StarsModal/components/Body';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import type { Dispatch } from '@swf/core/store';
import {
  BaseHeader,
  RadioGroup,
  TOURNFLAG_SERVINT_RECURRING_TOURN,
  useDeleteCashGame,
  useDeleteTournament,
  useHomeGamesWidget,
  useWidgetTheme
} from '@swf/poker';

import { HGGameType } from '../../../types/common';

import styles from './DeleteGame.scss';
import {
  TXTCLI_Are_you_sureX_All_entry_fees_f_ELL,
  TXTCLI_Are_you_sureX_Table_will_close_ELL,
  TXTCLI_Cancel_Table,
  TXTCLI_Cancel_Tournament,
  TXTCLI_Cancel_the_entire_series,
  TXTCLI_Cancel_this_occurrence_only,
  TXTCLI_Do_you_wish_to_cancel_a_single_ELL,
  TXTCLI_DonXt_cancel,
  TXTCLI_This_is_a_recurring_tournamentX
} from './DeleteGamePrompt.messages.json';

interface SearchParams {
  clubId: number;
  type: string;
  widgetId: string;
}

enum TournType {
  Recurrent = 'RECURRENT',
  Normal = 'NORMAL'
}

const radioOptions = [
  {
    value: TournType.Normal,
    label: (
      <div className={styles.option}>
        <Translation {...TXTCLI_Cancel_this_occurrence_only} />
      </div>
    )
  },
  {
    value: TournType.Recurrent,
    label: (
      <div className={styles.option}>
        <Translation {...TXTCLI_Cancel_the_entire_series} />
      </div>
    )
  }
];

const DeleteGamePrompt: React.FC = () => {
  const { clubId, type, widgetId } = useSearchParams<SearchParams>();
  const { widgetSelect } = useHomeGamesWidget(widgetId);
  const { modal } = useDispatch<Dispatch>();
  const [selectedOption, setSelectedOption] = useState(TournType.Normal);
  const [gameId] = useSelector(widgetSelect.selectedGames);
  const selectedTournamentServIntFlag = useSelector(widgetSelect.selectedTournamentServIntFlag);
  const { handleDeleteCashGame, loading } = useDeleteCashGame(widgetId);
  const { handleDeleteTournament, loading: loadingTournament } = useDeleteTournament(widgetId);
  const isCashGame = type === HGGameType.CashGame;
  const actionText = isCashGame ? TXTCLI_Cancel_Table : TXTCLI_Cancel_Tournament;
  const promptText = isCashGame
    ? TXTCLI_Are_you_sureX_Table_will_close_ELL
    : TXTCLI_Are_you_sureX_All_entry_fees_f_ELL;
  const { setStyle } = useContext<ModalContextValue>(ModalContext);
  const { className } = useWidgetTheme();

  useEffect(() => {
    setStyle({
      overlayClassName: className,
      className: styles.deleteTournModal,
      closeOnOverlayClick: true,
      closeOnEsc: true
    });
  }, [className, setStyle]);

  const isRecurring =
    selectedTournamentServIntFlag &&
    type === HGGameType[HGGameType['Tournament']] &&
    // eslint-disable-next-line no-bitwise
    (selectedTournamentServIntFlag & TOURNFLAG_SERVINT_RECURRING_TOURN) ===
      TOURNFLAG_SERVINT_RECURRING_TOURN;

  const handleSubmit = () => {
    if (isCashGame) {
      handleDeleteCashGame({
        body: { hgCancelCashGame: { clubId, tableId: Number(gameId) } }
      });
    }
    if (type === HGGameType[HGGameType['Tournament']]) {
      handleDeleteTournament({
        body: {
          hgCancelTournament: {
            clubId,
            tournId: Number(gameId),
            cancelRecurrence: selectedOption === TournType.Recurrent
          }
        }
      });
    }
  };

  const handleClose = () => {
    modal.close();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (event: ChangeEvent<any>) => {
    const { value } = event.target;
    setSelectedOption(value);
  };

  return (
    <>
      <BaseHeader headerClassName={styles.header} title={<Translation {...actionText} />} />
      <Body className={classNames(styles.body, styles.bodyLight)}>
        <Translation tagName="p" {...promptText} />
        {isRecurring && (
          <>
            <Translation tagName="h3" {...TXTCLI_This_is_a_recurring_tournamentX} />
            <Translation tagName="p" {...TXTCLI_Do_you_wish_to_cancel_a_single_ELL} />
            <RadioGroup
              disabled={loading}
              name="cancelTournType"
              options={radioOptions}
              selectedOption={selectedOption}
              onChange={handleChange}
            />
          </>
        )}
        <div className={styles.buttons}>
          <StarsButton
            variant="casino"
            disabled={loading || loadingTournament}
            onClick={handleClose}
            translation={TXTCLI_DonXt_cancel}
          />
          <StarsButton
            variant="primary"
            disabled={loading || loadingTournament}
            onClick={handleSubmit}
            translation={actionText}
          />
        </div>
      </Body>
    </>
  );
};

export default DeleteGamePrompt;
